import { useState } from "react";
import { useForm, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { AccountsFields } from "@/components/create/AccountsFields";
import { portfolioFormSchema, toAccountInputs, type PortfolioFormValues } from "@/lib/portfolio-schema";
import type { AccountPnl } from "@/lib/types";

interface Props {
  slug: string;
  accounts: AccountPnl[];
  onClose: () => void;
  onSaved: () => void;
}

export function EditAccountsDialog({ slug, accounts, onClose, onSaved }: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<PortfolioFormValues>({
    resolver: zodResolver(portfolioFormSchema) as unknown as Resolver<PortfolioFormValues>,
    defaultValues: {
      accounts: accounts.map((a) => ({
        label: a.label,
        avgCost: String(a.avgCost),
        shares: String(a.shares),
      })),
    },
    mode: "onTouched",
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitting(true);
    setServerError(null);
    try {
      const res = await fetch(`/api/portfolios/${slug}/accounts`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accounts: toAccountInputs(values.accounts) }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error ?? `HTTP ${res.status}`);
      onSaved();
      onClose();
    } catch (err) {
      setServerError((err as Error).message);
      setSubmitting(false);
    }
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-lg border bg-card p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Redigera konton</h2>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Stäng">
            <X className="size-4" />
          </Button>
        </div>
        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-5">
            <AccountsFields />
            {serverError && <p className="text-sm text-destructive">{serverError}</p>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Avbryt
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? "Sparar…" : "Spara"}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
